<template>
  <o-modal
    ref="modal"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.add'), type: 'confirm', disabled: isDisabled, callback: handleConfirm },
    ]"
    :valid="validate"
    modal-class="m-2 bg-gray-50 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-md sm:w-full h-full max-h-lg"
    content-class="mx-auto max-w-lg h-full py-4 px-4 flex flex-col"
  >
    <div v-if="serviceAutomation" class="text-left h-full flex flex-col">
      <div class="text-center">
        <h3 class="font-medium leading-6 text-md text-gray-700 dark:text-gray-200 mb-1">
          {{ $t('components.service.actions.addAutomation.title') }}
        </h3>
      </div>

      <div class="bg-white dark:bg-gray-900 border-t border-b -mx-8 mb-4 mt-6 flex-grow h-10">
        <o-scroll class="py-4 px-8 h-full">
          <o-form ref="form" class="h-full">
            <div class="flex space-x-4 mb-6">
              <o-field-group v-slot="{ status }" rules="required" class="w-full" :label="$t('components.service.actions.addAutomation.select')" :info="$t('components.service.actions.addAutomation.selectInfo')">
                <content-template-select
                  ref="contentTemplateSelect"
                  v-model="serviceAutomation.idContentTemplate"
                  :status="status"
                  v-model:template="contentTemplate"
                  class="w-full"
                  @createTemplate="onCreateTemplate"
                />
              </o-field-group>
            </div>

            <template v-if="serviceAutomation.idContentTemplate">
              <div class="flex space-x-4 mb-6">
                <o-field-group class="w-full" :label="$t('components.service.actions.editAutomation.sendViaEmail')">
                  <toggle-field v-model="_channels" value="email" />
                </o-field-group>
              </div>

              <!-- SMS Warning Message -->
              <div v-if="!isSmsChannelAvailable" class="flex space-x-4 mb-6">
                <o-alert variant="warning" class="w-full text-sm" show>
                  {{ $t('components.service.actions.editAutomation.smsBlankWarning') }}
                </o-alert>
              </div>

              <div class="flex space-x-4 mb-6">
                <o-field-group class="w-full" :label="$t('components.service.actions.addAutomation.sendViaSMS')" :info="$t('components.service.actions.addAutomation.smsRatesNote')">
                  <toggle-field v-model="_channels" value="sms" :disabled="!isSmsChannelAvailable" />
                </o-field-group>
              </div>
            </template>

            <div class="flex space-x-4 mb-6">
              <o-field-group class="w-full" :label="$t('general.frequency')" :info="$t('contentTemplate.serviceAutomation.frequencyInfo')">
                <select-field v-model="serviceAutomation.frequency" :options="serviceAutomationFrequencyOptions" />
              </o-field-group>
            </div>

            <div class="flex space-x-4 mb-6">
              <o-field-group class="w-full" :label="$t('service.requestMode')" :info="$t('service.requestModeInfo')">
                <select-field v-model="serviceAutomation.requirementMode" :options="serviceAutomationRequirementModeOptions" />
              </o-field-group>
            </div>

            <o-field-group
              v-if="isRequirementModeScheduled"
              v-slot="{ status }"
              :label="$t('service.scheduledTime')"
              :name="$t('service.scheduledTime')"
              :info="$t('contentTemplate.serviceAutomation.scheduledTimeInfo')"
              class="mb-4 block"
              rules="required"
            >
              <div class="flex items-center space-x-2">
                <number-field
                  v-model="serviceAutomation.scheduleOffsetValue"
                  type="number"
                  :status="status"
                  class="w-1/2"
                />
                <segment-field v-model="serviceAutomation.scheduleOffsetMode" :options="scheduleOffsetOptions" class="border w-1/2" />
              </div>
            </o-field-group>
          </o-form>
        </o-scroll>
      </div>
    </div>
  </o-modal>
</template>

<script setup lang="ts">
import ContentTemplateSelect from '@/components/contentTemplate/elements/Select.vue'
import { OAlert, OFieldGroup, OForm } from '@/components/elements'
import { TextField, SelectField, NumberField, SegmentField, ToggleField } from '@/components/fields'

import {
  ServiceAutomation,
  ServiceAutomationType,
  ServiceAutomationFrequency,
  ServiceAutomationFrequencyLabels,
  ServiceAutomationRequirementMode,
  ServiceAutomationRequirementModeLabels,
} from '~/_api/models/serviceAutomation'
import type { Service } from '~/_api/models/service'

const props = defineProps<{
  service: Service
  loading?: boolean
}>()

const emit = defineEmits<{
  (e: 'success'): void
  (e: 'error'): void
  (e: 'update:service', service: Service): void
  (e: 'update:loading', loading: boolean): void
}>()

const { $api, $notification, $i18n } = useNuxtApp()
const $t = $i18n.t.bind($i18n)

// Template refs
const modal = ref()
const form = ref()
const contentTemplateSelect = ref()

// State
const serviceAutomation = ref<ServiceAutomation | null>(null)
const contentTemplate = ref<any>(null)

const _channels = computed({
  get: () => serviceAutomation.value?.channels ?? ['email'],
  set: (value) => {
    if (serviceAutomation.value) {
      serviceAutomation.value.channels = value
    }
  },
})

const serviceAutomationFrequencyOptions = computed(() => {
  return Object.values(ServiceAutomationFrequency)
    .filter((value): value is ServiceAutomationFrequency => typeof value === 'number')
    .map((value) => ({
      label: $t(ServiceAutomationFrequencyLabels[value]),
      value,
    }))
})

const serviceAutomationRequirementModeOptions = computed(() => {
  return Object.values(ServiceAutomationRequirementMode)
    .filter((value): value is ServiceAutomationRequirementMode => typeof value === 'number')
    .map((value) => ({
      label: $t(ServiceAutomationRequirementModeLabels[value]),
      value,
    }))
})

const scheduleOffsetOptions = computed(() => [
  { label: $t('service.before'), value: 'before' },
  { label: $t('service.after'), value: 'after' },
])

const isDisabled = computed(() => !serviceAutomation.value)

const isRequirementModeScheduled = computed(() => {
  return serviceAutomation.value?.isRequirementModeScheduled
})

const isSmsChannelAvailable = computed(() => {
  return contentTemplate.value?.availableChannels?.includes('sms') ?? false
})

const setDefaults = () => {
  const data = {
    type: ServiceAutomationType.message,
    idContentTemplate: null,
    frequency: ServiceAutomationFrequency.reservation,
    requirementMode: ServiceAutomationRequirementMode.scheduled,
    scheduleOffset: 24,
    channels: ['email'],
  }

  serviceAutomation.value = new ServiceAutomation(data)
}

const open = () => {
  setDefaults()
  modal.value?.open()
}

const validate = () => {
  return form.value?.validate()
}

const handleConfirm = async () => {
  emit('update:loading', true)

  try {
    const response = await $api.service.addAutomation(props.service, serviceAutomation.value!)
    const service = response.data

    if (service) {
      $notification.toast({
        title: $t('components.service.actions.addAutomation.success'),
        text: $t('components.service.actions.addAutomation.successInfo', { title: serviceAutomation.value?.name }),
        variant: 'success',
      })

      emit('success')
      emit('update:service', service)
      emit('update:loading', false)

      return true
    }
  } catch (e) {
    $notification.error({
      title: $t('components.service.actions.addAutomation.error'),
      text: $t('components.service.actions.addAutomation.errorInfo', { title: serviceAutomation.value?.name }),
    })

    emit('update:loading', false)
    emit('error')
  }
}

const onCreateTemplate = () => {
  modal.value?.close()
}

defineExpose({
  open,
})
</script>
